import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import * as tus from 'tus-js-client';

import { CategoryPicker } from '@/features/catalog/CategoryPicker';
import { DISPLAY, TOPICS, TRADITIONS, slugify } from '@/features/composer/types';
import { PlayerSurface, type PlaybackPayload } from '@/features/watch/PlayerSurface';
import { useRequireRole } from '@/lib/auth/useRequireRole';
import { fetchPreviewPlayback } from '@/lib/playbackToken';
import { catalogMutations } from '@/lib/queries/catalog';
import { supabase } from '@/lib/supabase';
import { useUploadStore } from '@/stores/upload';

export const Route = createFileRoute('/creator/upload')({
  component: UploadRoute,
});

type Stage = 'select' | 'uploading' | 'processing' | 'metadata' | 'interrupted';

const ACCEPTED = ['video/mp4', 'video/quicktime', 'video/x-matroska', 'video/webm'];
const MAX_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB

interface UploadCreds {
  upload_url: string;
  video_id: string;
  upload_id: string;
  library_id: string;
  authorization_signature: string;
  authorization_expire: number;
}

function UploadRoute() {
  const { loading, user } = useRequireRole(['creator'], { creatorStatus: 'approved' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setActive = useUploadStore((s) => s.setActive);
  const updateProgress = useUploadStore((s) => s.updateProgress);

  const [stage, setStage] = useState<Stage>('select');
  const [creds, setCreds] = useState<UploadCreds | null>(null);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const uploadRef = useRef<tus.Upload | null>(null);
  const pollRef = useRef<number | null>(null);

  const [meta, setMeta] = useState({
    title: '',
    excerpt: '',
    slug: '',
    traditionTags: [] as string[],
    topicTags: [] as string[],
    categoryId: null as string | null,
  });
  const [previewPlayback, setPreviewPlayback] = useState<PlaybackPayload | null>(null);

  const beginPolling = useCallback(
    (uploadId: string) => {
      pollRef.current = window.setInterval(async () => {
        const { data } = await supabase
          .from('video_uploads')
          .select('upload_status, failure_reason')
          .eq('id', uploadId)
          .maybeSingle();
        if (data?.upload_status === 'ready') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setStage('metadata');
          updateProgress({ status: 'ready' });
        } else if (data?.upload_status === 'failed') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setFailure(data.failure_reason ?? 'Processing failed.');
          updateProgress({ status: 'failed' });
        }
      }, 5000);
    },
    [updateProgress],
  );

  useEffect(() => {
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  // Leave-and-return: rehydrate honestly from the background-upload store. The
  // tus.Upload instance did not survive the unmount, so an in-flight transfer is
  // truly interrupted (resumable by re-selecting the same file); processing and
  // ready survive server-side. The store's {uploadId, videoId, fileName} is
  // sufficient — the preview embed is minted server-side from the uploadId, so
  // no library creds need to persist (confirm 3).
  useEffect(() => {
    const active = useUploadStore.getState().active;
    if (!active) return;
    const rehydrated: UploadCreds = {
      upload_url: '',
      video_id: active.videoId,
      upload_id: active.uploadId,
      library_id: '',
      authorization_signature: '',
      authorization_expire: 0,
    };
    const titleFromName = active.fileName.replace(/\.[^.]+$/, '');
    if (active.status === 'processing') {
      setCreds(rehydrated);
      setMeta((m) => ({ ...m, title: titleFromName, slug: slugify(active.fileName) }));
      setStage('processing');
      beginPolling(active.uploadId);
    } else if (active.status === 'ready') {
      setCreds(rehydrated);
      setMeta((m) => ({ ...m, title: titleFromName, slug: slugify(active.fileName) }));
      setStage('metadata');
    } else if (active.status === 'uploading' || active.status === 'paused') {
      setStage('interrupted');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Owner-preview embed for the metadata stage (Token Auth requires a signed URL).
  useEffect(() => {
    if (stage !== 'metadata' || !creds) return;
    let alive = true;
    void fetchPreviewPlayback({ uploadId: creds.upload_id }).then((p) => {
      if (alive) setPreviewPlayback(p);
    });
    return () => {
      alive = false;
    };
  }, [stage, creds]);

  if (loading) return null;

  const startUpload = async (file: File) => {
    if (!ACCEPTED.includes(file.type)) {
      toast.error('Use MP4, MOV, MKV, or WebM.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Maximum file size is 5 GB.');
      return;
    }
    setFailure(null);

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      toast.error('Session expired. Sign in again.');
      return;
    }
    const res = await fetch(
      `${import.meta.env.PUBLIC_SUPABASE_URL}/functions/v1/bunny-create-upload`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ title: file.name }),
      },
    );
    if (!res.ok) {
      const e = (await res.json().catch(() => ({}))) as { error?: string };
      toast.error(
        e.error === 'not_approved_creator' ? 'Approval required.' : 'Upload setup failed.',
      );
      return;
    }
    const c = (await res.json()) as UploadCreds;
    setCreds(c);
    setStage('uploading');
    setMeta((m) => ({ ...m, title: file.name.replace(/\.[^.]+$/, ''), slug: slugify(file.name) }));

    const upload = new tus.Upload(file, {
      endpoint: c.upload_url,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      headers: {
        AuthorizationSignature: c.authorization_signature,
        AuthorizationExpire: String(c.authorization_expire),
        VideoId: c.video_id,
        LibraryId: c.library_id,
      },
      metadata: { filetype: file.type, title: file.name },
      // tus-js-client persists fingerprint→URL in localStorage by default,
      // which is what makes resume-after-refresh work.
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onError: (err) => {
        setFailure(err.message);
        updateProgress({ status: 'failed' });
        toast.error('Upload failed. You can retry the same file.');
      },
      onProgress: (uploaded, total) => {
        const pct = Math.round((uploaded / total) * 100);
        setProgress(pct);
        updateProgress({
          progress: pct,
          bytesUploaded: uploaded,
          bytesTotal: total,
          status: 'uploading',
        });
      },
      onSuccess: () => {
        setStage('processing');
        updateProgress({ status: 'processing', progress: 100 });
        beginPolling(c.upload_id);
      },
    });
    uploadRef.current = upload;
    setActive({
      uploadId: c.upload_id,
      videoId: c.video_id,
      fileName: file.name,
      progress: 0,
      status: 'uploading',
      bytesUploaded: 0,
      bytesTotal: file.size,
    });

    // Resume if a previous attempt for this file exists.
    const previous = await upload.findPreviousUploads();
    if (previous.length > 0) upload.resumeFromPreviousUpload(previous[0]!);
    upload.start();
  };

  const togglePause = () => {
    const u = uploadRef.current;
    if (!u) return;
    if (paused) {
      u.start();
      setPaused(false);
      updateProgress({ status: 'uploading' });
    } else {
      void u.abort();
      setPaused(true);
      updateProgress({ status: 'paused' });
    }
  };

  const cancelUpload = async () => {
    const u = uploadRef.current;
    if (u) await u.abort(true);
    setActive(null);
    setStage('select');
    setProgress(0);
  };

  const submit = async (asDraft: boolean) => {
    if (!user || !creds) return;
    if (
      !asDraft &&
      (!meta.title || meta.traditionTags.length === 0 || meta.topicTags.length === 0)
    ) {
      toast.error('Title, at least one tradition, and one topic are required to submit.');
      return;
    }
    const { data: content, error } = await supabase
      .from('content')
      .insert({
        creator_id: user.id,
        type: 'video',
        title: meta.title || 'Untitled video',
        slug: meta.slug || slugify(meta.title) || `video-${Date.now()}`,
        excerpt: meta.excerpt || null,
        video_bunny_id: creds.video_id,
        tradition_tags: meta.traditionTags,
        topic_tags: meta.topicTags,
        status: asDraft ? 'draft' : 'pending_review',
      })
      .select('id')
      .single();
    if (error) {
      toast.error(error.message);
      return;
    }
    // Category association goes through the gated catalog seam (PLAN §4):
    // a no-op until the schema lands, so it never blocks a real upload.
    await catalogMutations.attachCategoryToContent(content.id, meta.categoryId);
    await supabase
      .from('video_uploads')
      .update({ content_id: content.id })
      .eq('id', creds.upload_id);
    setActive(null);
    void queryClient.invalidateQueries({ queryKey: ['content', 'creator', user.id] });
    toast.success(asDraft ? 'Saved as draft.' : 'Submitted for review.');
    void navigate({ to: '/creator/content' });
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-serif text-3xl text-ink">Upload a video</h1>

      {stage === 'select' && (
        <label className="mt-8 flex h-64 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-ink/20 text-center hover:border-oxblood">
          <span className="font-sans text-sm text-ink/70">
            Drop a video file here or click to browse
          </span>
          <span className="mt-2 font-sans text-xs text-ink/40">
            MP4, MOV, MKV, or WebM — up to 5 GB
          </span>
          <input
            type="file"
            accept={ACCEPTED.join(',')}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void startUpload(f);
            }}
          />
        </label>
      )}

      {stage === 'uploading' && (
        <div className="mt-8">
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink/10">
            <div
              className="h-full bg-oxblood transition-[width]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="mt-3 font-sans text-sm text-ink/70">{progress}% uploaded</p>
          {failure && <p className="mt-2 text-sm text-signal">{failure}</p>}
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={togglePause}
              className="rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-oxblood"
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
            <button
              type="button"
              onClick={() => void cancelUpload()}
              className="rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-signal hover:text-signal"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {stage === 'processing' && (
        <div className="mt-8 rounded-lg border border-ink/10 bg-parchment p-8 text-center">
          <p className="font-serif text-lg text-ink">Processing your video</p>
          <p className="mt-3 font-sans text-sm text-ink/70">
            This usually takes 5–15 minutes depending on length. You can leave this page — the
            upload continues and we&apos;ll keep its place.
          </p>
          {failure && (
            <div className="mt-4">
              <p className="text-sm text-signal">{failure}</p>
              <button
                type="button"
                onClick={() => setStage('select')}
                className="mt-3 rounded-md border border-ink/20 px-3 py-1.5 text-sm text-ink hover:border-oxblood"
              >
                Upload a different file
              </button>
            </div>
          )}
        </div>
      )}

      {stage === 'interrupted' && (
        <div className="mt-8 rounded-lg border border-ink/10 bg-parchment p-8 text-center">
          <p className="font-serif text-lg text-ink">This upload was interrupted</p>
          <p className="mt-3 font-sans text-sm text-ink/70">
            Choose the same file and it resumes where it left off.
          </p>
          <label className="mt-4 inline-block cursor-pointer rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood">
            Choose file
            <input
              type="file"
              accept={ACCEPTED.join(',')}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void startUpload(f);
              }}
            />
          </label>
        </div>
      )}

      {stage === 'metadata' && creds && (
        <div className="mt-8 space-y-5">
          <div className="relative aspect-video overflow-hidden rounded-md bg-black">
            {previewPlayback ? (
              <PlayerSurface playback={previewPlayback} title={meta.title || 'Video preview'} />
            ) : (
              <div className="flex h-full items-center justify-center font-sans text-sm text-video-text-faint">
                Loading preview…
              </div>
            )}
          </div>
          <input
            value={meta.title}
            onChange={(e) =>
              setMeta((m) => ({ ...m, title: e.target.value, slug: slugify(e.target.value) }))
            }
            placeholder="Title"
            className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 font-serif text-lg text-ink focus:border-oxblood focus:outline-none"
          />
          <textarea
            value={meta.excerpt}
            onChange={(e) => setMeta((m) => ({ ...m, excerpt: e.target.value }))}
            placeholder="Description"
            rows={3}
            className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none"
          />
          <TagRow
            label="Traditions"
            options={TRADITIONS as readonly string[]}
            selected={meta.traditionTags}
            onToggle={(v) =>
              setMeta((m) => ({
                ...m,
                traditionTags: m.traditionTags.includes(v)
                  ? m.traditionTags.filter((t) => t !== v)
                  : [...m.traditionTags, v],
              }))
            }
          />
          <TagRow
            label="Topics"
            options={TOPICS as readonly string[]}
            selected={meta.topicTags}
            onToggle={(v) =>
              setMeta((m) => ({
                ...m,
                topicTags: m.topicTags.includes(v)
                  ? m.topicTags.filter((t) => t !== v)
                  : [...m.topicTags, v],
              }))
            }
          />
          <CategoryPicker
            value={meta.categoryId}
            onChange={(categoryId) => setMeta((m) => ({ ...m, categoryId }))}
          />
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => void submit(true)}
              className="rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood"
            >
              Save as draft
            </button>
            <button
              type="button"
              onClick={() => void submit(false)}
              className="rounded-md bg-ink px-4 py-2 text-sm font-medium text-parchment hover:bg-oxblood"
            >
              Submit for review
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TagRow({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="mb-1 font-sans text-xs uppercase tracking-[0.12em] text-ink/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              className={`rounded-sm border px-2 py-1 text-xs ${
                active
                  ? 'border-oxblood bg-oxblood/10 text-oxblood'
                  : 'border-ink/15 text-ink/70 hover:border-oxblood'
              }`}
            >
              {DISPLAY[o] ?? o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
